/* eslint-disable @typescript-eslint/no-use-before-define */
import Vue from 'vue'
import { ArrayUtil } from './ArrayUtil'
import { StringUtil } from './StringUtil'
import { Option, StatusUnstartStartedEnded } from 'kuaidong-model'
import { addWeeks, endOfDay, endOfWeek, endOfMonth, format, differenceInCalendarISOWeeks } from 'date-fns'

export type IDate = Date | string | number
export type IDateRange = [Date, Date]

export class DateWrapper {
  public date: Date

  constructor(date: IDate) {
    if (typeof date === 'string') {
      if (date.includes('T')) {
        this.date = new Date(date)
      } else {
        this.date = new Date(date.replace(/-/g, '/'))
      }
    } else if (typeof date === 'number') {
      this.date = new Date(date)
    } else {
      this.date = date
    }
  }

  /**
   * 显示 06-23 19:03
   */
  public get showStr(): string {
    return `${StringUtil.padZero(this.date.getMonth() + 1)}-${StringUtil.padZero(
      this.date.getDate()
    )} ${StringUtil.padZero(this.date.getHours())}:${StringUtil.padZero(this.date.getMinutes())}`
  }

  /**
   * 显示 06/23 19:03
   */
  public get showStr2() {
    return `${this.showDateStrShort3} ${this.showTimeStr}`
  }

  /**
   * 显示 06/23 (周三) 19:03
   */
  public get showStr3() {
    return `${this.showDateStrShort3} (${this.showWeekDayShort}) ${this.showTimeStr}`
  }

  public get showStrFullDate(): string {
    return `${this.showDateStr} ${this.showTimeStr}`
  }

  public get showFullStr(): string {
    return `${this.showDateStr} ${this.showFullTimeStr}`
  }

  /**
   * https://www.notion.so/58c1f47a482645b59b06b0119b1d0340
   *
   */
  public get showCustomStr(): string {
    let result = ''
    if (!this.isToday()) {
      result += `${this.date.getMonth() + 1}月${this.date.getDate()}日 `
    }
    if (this.isCurrentYear()) {
      const hour = this.date.getHours()
      const amPm = hour > 12 ? '下午' : '上午'
      const showHour = hour > 12 ? hour - 12 : hour
      result += `${amPm} ${showHour}:${StringUtil.padZero(this.date.getMinutes(), 2)}`
    } else {
      result = `${this.date.getFullYear()}年 ` + result
    }
    return result
  }

  public get showAmOrPm(): string {
    const hour = this.date.getHours()
    const amPm = hour > 12 ? '下午' : '上午'
    return amPm
  }

  public get showAmOrPmWithTime(): string {
    if (this.isToday()) {
      return `${this.showAmOrPm} ${this.showTimeStr}`
    } else {
      return this.showStr
    }
  }

  public isCurrentYear(): boolean {
    return this.date.getFullYear() === new Date().getFullYear()
  }

  public isToday(): boolean {
    return this.showDateStr === new DateWrapper(new Date()).showDateStr
  }

  public isYesterday(): boolean {
    return this.showDateStr === new DateWrapper(DateUtil.addDay(new Date(), -1)).showDateStr
  }
  /**
   * 显示 2020-03-12
   */
  public get showDateStr(): string {
    return `${StringUtil.padZero(this.date.getFullYear(), 4)}-${StringUtil.padZero(
      this.date.getMonth() + 1
    )}-${StringUtil.padZero(this.date.getDate())}`
  }

  /**
   * 显示 2020.3.12
   */
  public get showDateStr2(): string {
    return `${StringUtil.padZero(this.date.getFullYear(), 4)}.${this.showDateStrShort2}`
  }

  public get showMonthDateStr(): string {
    return `${this.date.getMonth() + 1}月${this.date.getDate()}日`
  }

  //  2022-6-19 --> 六月
  // 2022-1-7  -->  一月
  public get showMonthOfYearStrCN(): string {
    const month = new DateWrapper(this.date).date.getMonth()
    return DateUtil.MONTHS[month] + '月'
  }
  //  2022-6-19 --> 6月
  // 2022-1-7  -->  7 月
  public get showMonthOfYearStr(): string {
    const month = new DateWrapper(this.date).date.getMonth() + 1
    return month + '月'
  }
  //  2022-6-19 --> 19
  // 2022-1-7  --> 07
  public get showDateOfMonth(): string {
    return StringUtil.padZero(new DateWrapper(this.date).date.getDate())
  }

  public get showDateStrCN(): string {
    return `${this.date.getFullYear()}年${this.showDateStrShortCN}`
  }

  /**
   * 显示 03-12
   */
  public get showDateStrShort(): string {
    return `${StringUtil.padZero(this.date.getMonth() + 1)}-${StringUtil.padZero(this.date.getDate())}`
  }
  /**
   * 显示 03.12
   */
  public get showDateStrShort2(): string {
    return `${StringUtil.padZero(this.date.getMonth() + 1)}.${StringUtil.padZero(this.date.getDate())}`
  }

  /**
   * 显示 03/12
   */
  public get showDateStrShort3(): string {
    return `${StringUtil.padZero(this.date.getMonth() + 1)}/${StringUtil.padZero(this.date.getDate())}`
  }

  /**
   * 显示 03月12日
   */
  public get showDateStrShortZh(): string {
    return `${StringUtil.padZero(this.date.getMonth() + 1)}月${StringUtil.padZero(this.date.getDate())}日`
  }

  /**
   * 显示 2020-03-17（周三）
   */
  public get showDateWeekStr(): string {
    return `${this.showDateStr} (${this.showWeekDayShort})`
  }

  /**
   * 显示 03-17（周三）
   */
  public get showDateWeekStrShort(): string {
    return `${this.showDateStrShort3} (${this.showWeekDayShort})`
  }

  /**
   * 显示 周三 19:03
   */
  public get showWeekTimeStr(): string {
    return `${this.showWeekDayShort} ${this.showTimeStr}`
  }

  /**
   * 显示 3月12日
   */
  public get showDateStrShortCN(): string {
    return `${this.date.getMonth() + 1}月${this.date.getDate()}日`
  }

  /**
   * 显示分钟 19:03
   */
  public get showTimeStr(): string {
    return `${StringUtil.padZero(this.date.getHours())}:${StringUtil.padZero(this.date.getMinutes())}`
  }

  /**
   * 显示分钟和秒 19:03:13
   */
  public get showFullTimeStr(): string {
    return `${this.showTimeStr}:${StringUtil.padZero(this.date.getSeconds())}`
  }

  public get showMonthDayStr(): string {
    return `${this.date.getMonth() + 1}月${this.date.getDate()}日`
  }

  public get showYearMonthStr(): string {
    return `${this.date.getFullYear()}年${this.date.getMonth() + 1}月`
  }

  public get showYearMonthDayStr(): string {
    return `${this.date.getFullYear()}年${this.date.getMonth() + 1}月${this.date.getDate()}日`
  }

  public showYearMonthDayTimeStr(symbol = '.'): string {
    return `${this.date.getFullYear()}${symbol}${StringUtil.padZero(
      this.date.getMonth() + 1
    )}${symbol}${StringUtil.padZero(this.date.getDate())} ${this.showTimeStr}`
  }

  public get showMonthDayTimeStr(): string {
    return `${this.showDateStrShortCN} ${this.showTimeStr}`
  }

  public get showWeekDay(): string {
    return DateUtil.formatWeekday(this.date.getDay() as Weekday, '星期')
  }

  public get showWeekDayShort(): string {
    return DateUtil.formatWeekday(this.date.getDay() as Weekday, '周')
  }

  public get showWeekDayAbbr(): string {
    return DateUtil.formatWeekday(this.date.getDay() as Weekday)
  }

  public get showStr4() {
    if (!this.isCurrentYear()) return this.showYearMonthStr
    if (!this.isToday()) return this.showMonthDateStr
    return this.showTimeStr
  }

  public get showTimeSegment() {
    return DateUtil.timeSegmentName(this.date)
  }
}

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
export type WeekdayStr = '日' | '一' | '二' | '三' | '四' | '五' | '六'

export type Month = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 9 | 10 | 11 | 12
export type MonthStr = '一' | '二' | '三' | '四' | '五' | '六' | '七' | '八' | '九' | '十' | '十一' | '十二'

export class DateUtil {
  static WEEKDAYS: Weekday[] = [1, 2, 3, 4, 5, 6, 7]
  static WEEKDAYS_JS: Weekday[] = [0, 1, 2, 3, 4, 5, 6]

  static WEEKENDS: Weekday[] = [6, 7]
  static WEEKENDS_JS: Weekday[] = [0, 6]

  static WEEKDAYS_STR: WeekdayStr[] = ['一', '二', '三', '四', '五', '六', '日']
  static WEEKDAYS_STR_JS: WeekdayStr[] = ['日', '一', '二', '三', '四', '五', '六']

  static MONTHS: MonthStr[] = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二']

  static getWeekdaysStr(prefix = ''): string[] {
    return this.WEEKDAYS_STR.map((s) => `${prefix}${s}`)
  }

  static getWeekdaysStrJs(prefix = ''): string[] {
    return this.WEEKDAYS_STR_JS.map((s) => `${prefix}${s}`)
  }

  static formatWeekday(weekday: Weekday, prefix = '') {
    const s = weekday === 0 ? '日' : this.WEEKDAYS_STR[weekday - 1]
    return `${prefix}${s}`
  }

  static buildWeekdayOptions() {
    return this.WEEKDAYS.map((weekday) => ({
      label: this.formatWeekday(weekday, '周'),
      value: weekday,
    }))
  }

  // sleep， 传入毫秒
  public static async sleep(mills: number) {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve()
      }, mills)
    })
  }

  /**
   * 非标的格式变成Date
   * @param dateStr "2020-01-01 23:12:23" 这样的格式
   */
  public static fromString(dateStr: string | any): DateWrapper {
    if (typeof dateStr.getMonth === 'function') {
      // 如果传进来时间至二级处理
      return new DateWrapper(dateStr)
    }
    const splits = dateStr.split(' ')
    // 非标格式转换成iso8601， new Date 的参数在safari等浏览器里有严格限制
    if (dateStr.length === 19 && splits.length === 2) {
      dateStr = `${splits[0]}T${splits[1]}+08:00`
    }
    return new DateWrapper(new Date(dateStr))
  }

  /**
   * 统一返回DateWrapper
   * @param dateStr 标准的iso8601或者 如方法 `fromString` 这样的非标数据
   */
  public static parseFromString(dateStr: string): Date {
    if (!dateStr) {
      return new Date()
    }
    if (dateStr.includes('T')) {
      return new Date(dateStr)
    } else {
      return this.fromString(dateStr).date
    }
  }

  public static shortDateStr(dt: Date) {
    return new DateWrapper(dt).showStr
  }

  public static longDateStr(dt: Date) {
    return new DateWrapper(dt).showFullStr
  }

  /**
   * 毫秒转变成 汉化时间格式
   * @param milliseconds
   * return 汉化后的时间信息
   * @Example
     15 * 1000       15秒
     30 * 1000       30秒
     60 * 1000       1分钟
     62 * 1000       1分钟2秒
     16200 * 1000    4小时30分钟10秒
   */
  public static ms2StrCN(milliseconds: number, calcDay = false, roundSecond = false): string {
    if (milliseconds === 0) {
      return `0秒`
    }

    const seconds = milliseconds / 1000.0

    let day = 0
    let hour = Math.floor(seconds / 3600)
    if (calcDay) {
      day = Math.floor(hour / 24)
      hour = hour - day * 24
    }

    const minute = Math.floor((seconds - (day * 24 + hour) * 3600) / 60)
    const second = roundSecond ? Math.floor(seconds % 60) : seconds % 60

    const formatDay = day ? `${day}天` : ''
    const formatHour = hour ? `${hour}小时` : ''
    const formatMinute = minute ? `${minute}分钟` : ''
    const formatSecond = second ? `${second}秒` : ''

    return `${formatDay}${formatHour}${formatMinute}${formatSecond}`
  }

  public static ms2Str(millisecond: number, precision = 1, format: 'colon' | 'upper' = 'colon'): string {
    let seconds = millisecond / 1000.0
    let minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    seconds = seconds - minutes * 60
    minutes = minutes - hours * 60

    let result = `${seconds.toFixed(precision).padStart(precision ? precision + 3 : 2, '0')}`

    if (hours > 0) {
      result =
        format === 'colon'
          ? `${hours}:${minutes.toFixed(0).padStart(2, '0')}:${result}`
          : `${hours}'${minutes.toFixed(0).padStart(2, '0')}'${result}''`
    } else {
      result = format === 'colon' ? `${minutes}:${result}` : `${minutes}'${result}''`
    }

    return result
  }

  /**
   * 格式化
   * @param millisecond
   * @param precision
   * @param format
   * @returns 100:22.5
   */
  public static ms2Minute(millisecond: number, precision = 1, format: 'colon' | 'upper' = 'colon') {
    const seconds = millisecond / 1000.0
    const minutes = Math.floor(seconds / 60)

    const result = (seconds - minutes * 60).toFixed(precision).padStart(precision ? precision + 3 : 2, '0')

    return format === 'colon' ? `${minutes}:${result}` : `${minutes}'${result}''`
  }

  public static ms2FullStr(millisecond: number, precision = 0): string {
    let seconds = millisecond / 1000.0
    let minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    seconds = seconds - minutes * 60
    minutes = minutes - hours * 60
    const result = `${seconds.toFixed(precision).padStart(precision ? precision + 3 : 2, '0')}`
    return `${hours.toFixed(0).padStart(2, '0')}:${minutes.toFixed(0).padStart(2, '0')}:${result}`
  }

  // 自然月累加，例如：1.31 + 1 = 2.28， 1.31 + 2 = 3.31
  public static addMonth(_dt: IDate, diff = 1) {
    const date = new DateWrapper(_dt).date
    const year = date.getFullYear()
    const month = date.getMonth()
    const day = date.getDate()
    const h = date.getHours()
    const m = date.getMinutes()
    const s = date.getSeconds()
    const next_month = month + diff
    const next_month_mod = next_month % 12
    // 下一个月是2月份
    if (next_month_mod == 1) {
      if (DateUtil.isLeapYear(date)) {
        return day > 29 ? new Date(year, next_month, 29, h, m, s) : new Date(year, next_month, day, h, m, s)
      } else {
        return day > 28 ? new Date(year, next_month, 28, h, m, s) : new Date(year, next_month, day, h, m, s)
      }
    } else if ([1, 3, 5, 8, 10].includes(next_month_mod)) {
      // 下一个月是30天的月份
      return day > 30 ? new Date(year, next_month, 30, h, m, s) : new Date(year, next_month, day, h, m, s)
    } else {
      return new Date(year, next_month, day, h, m, s)
    }
  }

  public static addDay(_dt: IDate, _days = 1) {
    const dt = new DateWrapper(_dt).date
    return new Date(dt.getTime() + _days * 86400000)
  }

  public static addHours(_dt: IDate, _hours = 1) {
    const dt = new DateWrapper(_dt).date
    return new Date(dt.getTime() + _hours * 3600000)
  }

  public static addMinutes(_dt: IDate, _minutes = 1) {
    const dt = new DateWrapper(_dt).date
    return new Date(dt.getTime() + _minutes * 60000)
  }

  public static addSeconds(_dt: IDate, _seconds = 1) {
    const dt = new DateWrapper(_dt).date
    return new Date(dt.getTime() + _seconds * 1000)
  }
  public static isLeapYear(_dt: Date) {
    const year = _dt.getFullYear()
    if (year % 100 === 0) {
      return year % 400 === 0
    } else {
      return year % 4 === 0
    }
  }

  public static isFuture(_dt: IDate, _standardDt?: IDate) {
    const dt = new DateWrapper(_dt).date
    const standardDt = new DateWrapper(_standardDt || new Date()).date
    return dt.getTime() > standardDt.getTime()
  }

  public static isInSameMonth(_dt: IDate, _standardDt?: IDate) {
    const dt = new DateWrapper(_dt).date
    const standardDt = new DateWrapper(_standardDt || new Date()).date
    return this.isToday(this.beginningOfMonth(dt), this.beginningOfMonth(standardDt))
  }

  public static isInSameWeek(_dt: IDate, _standardDt?: IDate) {
    const dt = new DateWrapper(_dt).date
    const standardDt = new DateWrapper(_standardDt || new Date()).date
    return this.isToday(this.beginningOfWeek(dt), this.beginningOfWeek(standardDt))
  }

  public static isToday(_dt: IDate, _standardDt?: IDate) {
    const dt = new DateWrapper(_dt).date
    const standardDt = new DateWrapper(_standardDt || new Date()).date

    return (
      dt.getFullYear() === standardDt.getFullYear() &&
      dt.getMonth() === standardDt.getMonth() &&
      dt.getDate() === standardDt.getDate()
    )
  }

  public static isPast(_dt: IDate, _standardDt?: IDate) {
    const dt = new DateWrapper(_dt).date
    const standardDt = new DateWrapper(_standardDt || new Date()).date
    return dt.getTime() < standardDt.getTime()
  }

  public static beginningOfDay(_dt?: IDate) {
    const dt = new DateWrapper(_dt || new Date()).date
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())
  }

  public static beginningOfWeek(_dt?: IDate) {
    const dt = this.beginningOfDay(_dt)
    let days = dt.getDay()
    if (days === 0) {
      days = 7
    }
    return this.addDay(dt, -1 * (days - 1))
  }

  public static beginningOfMonth(_dt?: IDate) {
    const dt = this.beginningOfDay(_dt)
    const days = dt.getDate()
    return this.addDay(dt, -1 * (days - 1))
  }

  // 获得下个月的月初时间
  public static beginningOfNextMonth(_dt?: IDate) {
    return this.beginningOfMonth(this.addDay(this.beginningOfMonth(_dt), 32))
  }

  // 获得下个周的周初时间
  public static beginningOfNextWeek(_dt?: IDate) {
    return this.beginningOfWeek(this.addDay(this.beginningOfWeek(_dt), 8))
  }

  public static endOfWeek(_dt?: IDate) {
    const dt = new Date(_dt || new Date())
    return endOfWeek(dt, { weekStartsOn: 1 })
  }

  public static endOfMonth(_dt?: IDate) {
    const dt = new Date(_dt || new Date())
    return endOfMonth(dt)
  }

  public static endOfDay(_dt?: IDate) {
    const dt = new Date(_dt || new Date())
    return endOfDay(dt)
  }

  public static sameDayOfWeek(_dt?: IDate) {
    return this.addDay(this.beginningOfWeek(_dt), (new Date().getDay() || 7) - 1)
  }

  public static getRestDayOfWeek(dt: Date = new Date()): number {
    const dayOfWeek = dt.getDay()
    return dayOfWeek === 0 ? 0 : 7 - dayOfWeek
  }

  public static isDayInDifferentWeek(left: Date, right: Date) {
    return differenceInCalendarISOWeeks(left, right) !== 0
  }

  public static timeSegmentName(date: Date = new Date()): string {
    const hours = date.getHours()
    if (hours < 12) {
      return '上午'
    } else if (hours < 19) {
      return '下午'
    } else {
      return '晚上'
    }
  }

  public static second2Minute(seconds: number, precision = 1) {
    return +(seconds / 60).toFixed(precision)
  }

  public static second2Hour(seconds: number) {
    return this.millisecond2Hour(seconds * 1000)
  }

  public static millisecond2Hour(milliseconds: number, precision = 1) {
    return +(milliseconds / (60 * 60 * 1000.0)).toFixed(precision)
  }

  public static second1timeStr(seconds: number) {
    const hour = Math.floor(seconds / 3600)
    const minute = Math.floor((seconds % 3600) / 60)
    const second = Math.floor(seconds % 60)

    const result = ArrayUtil.trimLeft([hour, minute, second])

    if (result.length === 0) {
      result.push(0, 0)
    }
    if (result.length === 1) {
      result.unshift(0)
    }

    return result.map((v) => `${v}`.padStart(2, '0')).join(':')
  }

  // 秒转换为时间不保留小数，格式为： '04:00'
  // 时间不大于24小时
  public static second2timeStr(seconds: number) {
    const hour = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, '0')
    const minute = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0')
    return `${hour}:${minute}`
  }

  public static second3timeStr(seconds: number) {
    const hour = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, '0')
    const minute = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0')
    const second = Math.round(seconds % 60)
      .toString()
      .padStart(2, '0')
    return `${hour}:${minute}:${second}`
  }

  public static second4timeStr(seconds: number) {
    const minute = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0')
    const second = (seconds % 60).toString().padStart(2, '0')
    return `${minute}:${second}`
  }

  // 毫秒数变成分钟，保留一位小数
  public static millisecond2minutes(milliseconds: number) {
    if (!milliseconds) {
      return 0
    }
    return Math.round(milliseconds / 1000 / 6) / 10
  }

  public static millisecond2rankstr(v: number) {
    return `${~~(v / 60 / 1000)}分${((~~(v / 100) / 10) % 60).toFixed(1).padStart(4, '0')}秒`
  }

  // 毫秒转换为时间不保留小数，格式为： '1:30' | '0:30'
  // 时间不大于 1小时
  public static millisecond2timeStr(milliseconds: number) {
    const seconds = Math.floor(milliseconds / 1000)
    if (seconds < 60) {
      return `0:${seconds}`
    } else {
      const minute = Math.floor(seconds / 60)
      const second = Math.floor(seconds % 60)
      const padSecond = second.toString().padStart(2, '0')
      return `${minute}:${padSecond}`
    }
  }

  public static buildDateRangePickerOptions() {
    return {
      shortcuts: [
        {
          text: '今天',

          onClick(picker: Vue) {
            const date = new Date()

            picker.$emit('pick', [date, date])
          },
        },
        {
          text: '昨天',

          onClick(picker: Vue) {
            const date = DateUtil.addDay(new Date(), -1)

            picker.$emit('pick', [date, date])
          },
        },
        {
          text: '本周',

          onClick(picker: Vue) {
            picker.$emit('pick', DateRangeUtil.curWeek())
          },
        },
        {
          text: '本月',

          onClick(picker: Vue) {
            picker.$emit('pick', DateRangeUtil.curMonth())
          },
        },
        {
          text: '近7天',

          onClick(picker: Vue) {
            picker.$emit('pick', DateRangeUtil.last7Days())
          },
        },
      ],

      disabledDate(date: Date) {
        return date > new Date()
      },
    }
  }

  public static formatTime(seconds: number): string {
    seconds = Math.round(seconds)

    const m = StringUtil.padZero(Math.floor(seconds / 60))
    const s = StringUtil.padZero(Math.floor(seconds % 60))

    return `${m}:${s}`
  }

  public static formatTimeRound(_date = new Date(), _format = 'HH:mm', _step = 10) {
    const result = format(_date, _format).split(':').map(Number)

    result[result.length - 1] = Math.floor(result[result.length - 1] / _step) * _step

    return result.map((item) => StringUtil.padZero(item, 2)).join(':')
  }

  public static formatDuration(seconds: number): string {
    seconds = Math.round(seconds)
    const s = seconds % 60
    const m = Math.floor(seconds / 60) % 60
    const h = Math.floor(seconds / 60 / 60) % 24
    const d = Math.floor(seconds / 60 / 60 / 24)

    return `${d}天${h}小时${m}分${s}秒`
  }

  public static formatDurationArr(seconds: number): number[] {
    seconds = Math.round(seconds)
    const s = seconds % 60
    const m = Math.floor(seconds / 60) % 60
    const h = Math.floor(seconds / 60 / 60) % 24
    const d = Math.floor(seconds / 60 / 60 / 24)

    return [d, h, m, s]
  }

  public static formatMinute(v: number): string {
    const seconds = Math.round(v / 1000)
    const m = Math.floor(seconds / 60) % 60
    const h = Math.floor(seconds / 60 / 60) % 24
    const d = Math.floor(seconds / 60 / 60 / 24)

    let result = ``
    result = `${m}分${result}`
    if (h !== 0) {
      result = `${h}小时${result}`
    }
    if (d !== 0) {
      result = `${d}天${result}`
    }
    return result
  }

  public static formatShortDuration(seconds: number): string {
    seconds = Math.round(seconds)
    const s = seconds % 60
    const m = Math.floor(seconds / 60) % 60
    const h = Math.floor(seconds / 60 / 60) % 24
    const d = Math.floor(seconds / 60 / 60 / 24)

    let result = `${s}秒`
    if (m !== 0) {
      result = `${m}分${result}`
    }
    if (h !== 0) {
      result = `${h}小时${result}`
    }
    if (d !== 0) {
      result = `${d}天${result}`
    }
    return result
  }

  public static formatHMSDuration(seconds: number): number[] {
    seconds = Math.round(seconds)
    const s = seconds % 60
    const m = Math.floor(seconds / 60) % 60
    const h = Math.floor(seconds / 60 / 60)
    return [h, m, s]
  }

  public static formatHMSMDuration(milliseconds: number): number[] {
    const mills = milliseconds % 1000
    const seconds = Math.floor(milliseconds / 1000)
    const s = seconds % 60
    const m = Math.floor(seconds / 60) % 60
    const h = Math.floor(seconds / 60 / 60)
    return [h, m, s, mills]
  }

  public static formatTinyDuration(seconds: number): string {
    seconds = Math.round(seconds)
    const s = seconds % 60
    const m = Math.floor(seconds / 60) % 60
    const h = Math.floor(seconds / 60 / 60) % 24
    const d = Math.floor(seconds / 60 / 60 / 24)

    if (d !== 0) return `${d}天${h}小时`
    if (h !== 0) return `${h}小时${m}分`
    if (m !== 0) return `${m}分${s}秒`
    return `${s}秒`
  }

  public static buildDatesFromRange(startDateStr: IDate, endDateStr: IDate) {
    let startDate = new DateWrapper(startDateStr).date
    const endDate = new DateWrapper(endDateStr).date

    const result: Date[] = []

    do {
      result.push(startDate)
      startDate = this.addDay(startDate, 1)
    } while (startDate <= endDate)

    return result
  }

  public static buildWeeksFromRange(startDate: IDate, endDate: IDate) {
    const from = new DateWrapper(startDate).date
    const to = new DateWrapper(endDate).date

    let count = 0

    const result: { weekno: number; startDate: Date; endDate: Date }[] = []

    for (let date = from; date <= to; ) {
      const endOfWeek = DateUtil.endOfWeek(date)

      result.push({
        weekno: count++,
        startDate: new DateWrapper(date).date,
        endDate: new DateWrapper(Math.min(endOfWeek.getTime(), to.getTime())).date,
      })

      date = DateUtil.beginningOfDay(DateUtil.addDay(endOfWeek, 1))
    }

    return result
  }

  public static buildWeekdaysFromRange(startDateStr: string, endDateStr: string, isWeekdaysJs = false) {
    const dates = this.buildDatesFromRange(startDateStr, endDateStr)

    return dates.map((date) => (isWeekdaysJs ? date.getDay() : date.getDay() || 7))
  }

  public static diffInMilliSeconds(startDate: Date, endDate: Date) {
    return endDate.getTime() - startDate.getTime()
  }

  public static diffInSeconds(startDate: Date, endDate: Date) {
    return Math.floor((endDate.getTime() - startDate.getTime()) / 1000)
  }

  public static diffInHour(startDate: Date, endDate: Date) {
    return Math.floor((endDate.getTime() - startDate.getTime()) / (60 * 60 * 1000))
  }

  public static diffInMins(startDate: Date, endDate: Date) {
    return Math.floor((endDate.getTime() - startDate.getTime()) / (60 * 1000))
  }

  public static diffInDay(startDate: Date, endDate: Date) {
    startDate = this.beginningOfDay(startDate)
    endDate = this.beginningOfDay(endDate)

    return Math.floor((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000))
  }

  public static diffInDayCur(startDate: Date, endDate: Date) {
    return Math.floor((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000))
  }

  public static diffDayHourMins(startDate: Date, endDate: Date) {
    const day = this.diffInDayCur(startDate, endDate)
    const hours = this.diffInHour(startDate, endDate) - day * 24
    const mins = this.diffInMins(startDate, endDate) - this.diffInHour(startDate, endDate) * 60
    return [day, hours, mins]
  }

  public static beginningOfNthWeekOfYear(year: number, week: number) {
    return this.beginningOfWeek(addWeeks(new Date(year, 0, 1), week - 1))
  }

  // 给定日期，是当月的第几周， 返回值从1开始， 周的概念，周一开始
  public static weekIndexOfMonth(date: Date): number {
    const monthStart = this.beginningOfMonth(date)
    const endOfFirstWeek = this.endOfWeek(monthStart)
    const targetMonthday = date.getDate()
    if (targetMonthday <= endOfFirstWeek.getDate()) {
      return 1
    } else {
      return Math.floor((targetMonthday - endOfFirstWeek.getDate()) / 7) + 2
    }
  }

  public static nthWeekOfYear(date = new Date()) {
    const beginningOfFirstWeek = this.beginningOfWeek(new Date(date.getFullYear(), 0, 1))
    const beginningOfCurrentWeek = this.beginningOfWeek(date)

    return Math.floor(this.diffInDay(beginningOfFirstWeek, beginningOfCurrentWeek) / 7) + 1
  }

  public static buildTimeOptions(_format = 'HH:mm', _step = 10) {
    const curr = _format.slice(0, 2)
    const next = _format.slice(3)

    const result: Option<string>[] = []

    let max = 0

    if (curr == 'ss' || curr == 'mm') {
      max = 60
    } else if (curr == 'HH') {
      max = 24
    } else if (curr == 'hh') {
      max = 12
    }

    const step = next ? 1 : _step
    const children = next ? this.buildTimeOptions(next, _step) : undefined

    for (let i = 0; i < max; i += step) {
      const value = StringUtil.padZero(i, 2)

      result.push({
        label: value,
        value: value,
        children: children ? JSON.parse(JSON.stringify(children)) : children,
      })
    }

    return result
  }

  /**
   *
   * @param date new Date(2022-02-03)
   * @param time 10:10
   * @returns 2022-02-03 10:10
   */
  public static mergeDateAndTime(_date: Date, _time: string) {
    const date = this.beginningOfDay(_date)

    const [hours, minutes, seconds] = _time.split(':').map(Number)

    date.setHours(hours || 0, minutes || 0, seconds || 0)

    return date
  }

  public static min(...dates: IDate[]) {
    return new DateWrapper(Math.min(...dates.map((d) => new DateWrapper(d).date.getTime()))).date
  }

  public static max(...dates: IDate[]) {
    return new DateWrapper(Math.max(...dates.map((d) => new DateWrapper(d).date.getTime()))).date
  }

  public static calcStatus(startDate: string | Date, endDate: string | Date, onlyDate = true) {
    const today = new Date()

    let fromDate = new DateWrapper(startDate).date
    let toDate = new DateWrapper(endDate).date

    if (onlyDate) {
      fromDate = DateUtil.beginningOfDay(fromDate)
      toDate = DateUtil.endOfDay(toDate)
    }

    if (today < fromDate) {
      return StatusUnstartStartedEnded.Unstart
    } else if (today <= toDate) {
      return StatusUnstartStartedEnded.Started
    } else {
      return StatusUnstartStartedEnded.Ended
    }
  }

  public static nthWeekOfMonth(date: Date = new Date()) {
    const startOfMonth = DateUtil.beginningOfMonth(date)
    // 补全本周天数,然后除7
    const weekday = startOfMonth.getDay()
    const days = date.getDate() + (weekday ? weekday - 1 : 6)
    return Math.ceil(days / 7)
  }

  public static groupByWeek<T>(_data: T[], _dateKey: keyof T, _needSort = false, _sortByDesc = true) {
    if (!_data.length) {
      return []
    }

    const data = _data

    if (_needSort) {
      data.sort((i1, i2) => {
        const d1 = new DateWrapper(i1[_dateKey] as any).date.getTime()
        const d2 = new DateWrapper(i2[_dateKey] as any).date.getTime()

        return d1 - d2
      })
    }

    const d1 = data[0][_dateKey] as any
    const d2 = data[data.length - 1][_dateKey] as any
    const weeks: Array<
      ReturnType<typeof DateUtil['buildWeeksFromRange']>[number] & { items: T[] }
    > = this.buildWeeksFromRange(d1, d2).map((w) => ({
      ...w,
      startDate: DateUtil.beginningOfWeek(w.startDate),
      endDate: DateUtil.endOfWeek(w.endDate),
      items: [],
    }))

    if (_sortByDesc) {
      data.reverse()
      weeks.reverse()
    }

    for (const week of weeks) {
      for (const item of data) {
        if (DateRangeUtil.in(item[_dateKey] as any, week.startDate, week.endDate)) {
          week.items.push(item)
        }
      }
    }

    return weeks.filter((w) => w.items.length)
  }
}

export class DateRangeUtil {
  public static in(_date: IDate, _startDate: IDate, _endDate: IDate, _onlyDate = false) {
    const date = new DateWrapper(_date).date
    let startDate = new DateWrapper(_startDate).date
    let endDate = new DateWrapper(_endDate).date

    if (_onlyDate) {
      startDate = DateUtil.beginningOfDay(startDate)
      endDate = DateUtil.endOfDay(endDate)
    }

    return date >= startDate && date <= endDate
  }

  public static lastNDays(n: number, date = new Date()) {
    return [DateUtil.addDay(date, -1 * n), date]
  }

  public static last7Days(date = new Date()) {
    return this.lastNDays(7, date)
  }

  public static curWeek(): IDateRange {
    return [DateUtil.beginningOfWeek(), new Date()]
  }

  public static curMonth(): IDateRange {
    return [DateUtil.beginningOfMonth(), new Date()]
  }

  public static lastWeek(): IDateRange {
    const today = new Date()
    const oneWeek = 3600 * 1000 * 24 * 7
    const end = DateUtil.endOfWeek(today)
    const start = DateUtil.beginningOfWeek(today)
    start.setTime(start.getTime() - oneWeek)
    end.setTime(end.getTime() - oneWeek)

    return [start, end]
  }

  public static lastMonth(): IDateRange {
    const today = new Date()
    const year = today.getFullYear()
    const currentMonth = today.getMonth()
    if (currentMonth > 0) {
      const dateTime = new Date(year, currentMonth - 1)
      const end = DateUtil.endOfMonth(dateTime)
      const start = DateUtil.beginningOfMonth(dateTime)
      return [start, end]
    } else {
      const dateTime = new Date(year - 1, 11)
      const end = DateUtil.endOfMonth(dateTime)
      const start = DateUtil.beginningOfMonth(dateTime)
      return [start, end]
    }
  }

  // 判断给定的两个时间范围，日期是否相等
  public static isDateRangeEqual(someDate: IDateRange, otherDate: IDateRange) {
    return (
      new DateWrapper(someDate[0]).showDateStr === new DateWrapper(otherDate[0]).showDateStr &&
      new DateWrapper(someDate[1]).showDateStr === new DateWrapper(otherDate[1]).showDateStr
    )
  }

  // 判断给定日期是否处于当年的某段日期范围内
  public static isInDateRangeOfYear(
    someDate: Date,
    start: { month: number; day: number },
    end: { month: number; day: number }
  ): boolean {
    const year = someDate.getFullYear()
    // Date month 参数为 monthIndex ， 需要按照实际减一
    const startDate = new Date(year, start.month - 1, start.day)
    const endDate = new Date(year, end.month - 1, end.day)
    return startDate < someDate && someDate < endDate
  }
}
